import React, { useState, useEffect } from "react";

// Faixas fixas: 5 indicadores × 0-3 pontos cada = 0-15 no total, divididos em
// 4 faixas de 4 pontos. Os NÚMEROS ficam fixos aqui (não confiamos no modelo
// pra fazer essa aritmética) — o texto de cada faixa vem do material gerado
// (`faixasSignificado`), pareado por índice com estes limites.
const FAIXAS_LIMITES = [
  [0, 3],
  [4, 7],
  [8, 11],
  [12, 15],
];

const SEMANAS = [1, 2, 3, 4];

function emptyScores() {
  return { 1: [null, null, null, null, null], 2: [null, null, null, null, null], 3: [null, null, null, null, null], 4: [null, null, null, null, null] };
}

function loadScores(storageKey) {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return emptyScores();
    const parsed = JSON.parse(raw);
    const base = emptyScores();
    for (const semana of SEMANAS) {
      if (Array.isArray(parsed[semana]) && parsed[semana].length === 5) {
        base[semana] = parsed[semana];
      }
    }
    return base;
  } catch {
    return emptyScores();
  }
}

function faixaIndexParaSoma(soma) {
  for (let i = 0; i < FAIXAS_LIMITES.length; i++) {
    const [min, max] = FAIXAS_LIMITES[i];
    if (soma >= min && soma <= max) return i;
  }
  return FAIXAS_LIMITES.length - 1;
}

/**
 * @param {{ instrucoes: string, indicadores: string[], faixasSignificado: string[], storageKey: string }} props
 */
export default function ProgressoJornada({ instrucoes, indicadores, faixasSignificado, storageKey }) {
  const [scores, setScores] = useState(() => loadScores(storageKey));
  const [semanaAtiva, setSemanaAtiva] = useState(1);

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(scores));
  }, [scores, storageKey]);

  const setScore = (indicadorIdx, valor) => {
    setScores(prev => ({
      ...prev,
      [semanaAtiva]: prev[semanaAtiva].map((v, i) => (i === indicadorIdx ? valor : v)),
    }));
  };

  const somaSemana = (semana) => {
    const vals = scores[semana];
    if (vals.every(v => v === null)) return null;
    return vals.reduce((acc, v) => acc + (v || 0), 0);
  };

  const somaAtiva = somaSemana(semanaAtiva);
  const faixaAtivaIdx = somaAtiva === null ? null : faixaIndexParaSoma(somaAtiva);

  return (
    <>
      <section className="space-y-4 print:hidden" data-testid="progresso-jornada">
        <h2 className="text-[#1E3A8A] font-outfit font-semibold text-[17px] uppercase tracking-wide">Seu progresso semana a semana</h2>

        {instrucoes && <p className="text-gray-600 font-manrope text-[15px] leading-relaxed">{instrucoes}</p>}

        <div className="flex gap-2" data-testid="progresso-tabs">
          {SEMANAS.map(semana => {
            const soma = somaSemana(semana);
            const isActive = semana === semanaAtiva;
            return (
              <button
                key={semana}
                data-testid={`progresso-tab-semana-${semana}`}
                onClick={() => setSemanaAtiva(semana)}
                className={`flex-1 rounded-xl px-2 py-2.5 text-center font-manrope text-[13px] transition-all ${
                  isActive ? "bg-[#1E3A8A] text-white" : "bg-gray-100 text-gray-600 hover:bg-[#DBEAFE]"
                }`}
              >
                <div className="font-semibold">Semana {semana}</div>
                <div className={`text-[11px] ${isActive ? "text-white/80" : "text-gray-400"}`}>{soma === null ? "—" : `${soma} pts`}</div>
              </button>
            );
          })}
        </div>

        <div className="bg-white rounded-2xl p-4 space-y-3">
          {indicadores.map((indicador, i) => (
            <div key={i} className="space-y-1.5">
              <p className="text-gray-800 font-manrope text-[15px]">{indicador}</p>
              <div className="flex gap-1.5" data-testid={`progresso-indicador-${i}`}>
                {[0, 1, 2, 3].map(valor => {
                  const isChosen = scores[semanaAtiva][i] === valor;
                  return (
                    <button
                      key={valor}
                      data-testid={`progresso-score-${i}-${valor}`}
                      onClick={() => setScore(i, valor)}
                      className={`w-9 h-9 rounded-full text-sm font-manrope font-semibold transition-all ${
                        isChosen ? "bg-[#1E3A8A] text-white scale-105" : "bg-gray-100 text-gray-500 hover:bg-[#DBEAFE]"
                      }`}
                    >
                      {valor}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="bg-[#DBEAFE] rounded-2xl p-4 space-y-1" data-testid="progresso-resultado">
          {somaAtiva === null ? (
            <p className="text-[#1E3A8A] font-manrope text-[15px]">Marque os 5 indicadores acima para ver o que sua pontuação desta semana significa.</p>
          ) : (
            <>
              <p className="text-[#1E3A8A] font-manrope font-semibold text-[15px]">Semana {semanaAtiva}: {somaAtiva} de 15 pontos</p>
              <p className="text-[#1E3A8A] font-manrope text-[15px] leading-relaxed">{faixasSignificado[faixaAtivaIdx]}</p>
            </>
          )}
        </div>
      </section>

      {/* Versão só-impressão: tabela em branco pra preencher à mão (a tela
          acima, interativa, não faz sentido no papel — fica print:hidden). */}
      <section className="hidden print:block space-y-3" data-testid="progresso-jornada-print">
        <h2 className="text-black font-outfit font-semibold text-[17px] uppercase tracking-wide">Seu progresso semana a semana</h2>
        {instrucoes && <p className="text-black font-manrope text-[15px] leading-relaxed">{instrucoes}</p>}
        <p className="text-black font-manrope text-[13px] italic">Marque de 0 a 3 cada indicador, toda semana. No fim de cada semana, some os 5 valores e confira o significado da faixa na tabela abaixo.</p>

        <table className="w-full border-collapse text-black font-manrope text-[13px]">
          <thead>
            <tr>
              <th className="border border-gray-400 p-2 text-left">Indicador</th>
              {SEMANAS.map(semana => (
                <th key={semana} className="border border-gray-400 p-2 w-16 text-center">Semana {semana}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {indicadores.map((indicador, i) => (
              <tr key={i}>
                <td className="border border-gray-400 p-2">{indicador}</td>
                {SEMANAS.map(semana => (
                  <td key={semana} className="border border-gray-400 p-2" />
                ))}
              </tr>
            ))}
            <tr>
              <td className="border border-gray-400 p-2 font-semibold">Total da semana (some os 5 valores)</td>
              {SEMANAS.map(semana => (
                <td key={semana} className="border border-gray-400 p-2" />
              ))}
            </tr>
          </tbody>
        </table>

        <table className="w-full border-collapse text-black font-manrope text-[13px] mt-2">
          <thead>
            <tr>
              <th className="border border-gray-400 p-2 w-24 text-left">Total</th>
              <th className="border border-gray-400 p-2 text-left">O que significa</th>
            </tr>
          </thead>
          <tbody>
            {FAIXAS_LIMITES.map(([min, max], i) => (
              <tr key={i}>
                <td className="border border-gray-400 p-2 font-semibold">{min}–{max}</td>
                <td className="border border-gray-400 p-2">{faixasSignificado[i]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </>
  );
}
